import type { ReactNode } from 'react';

/**
 * A small inline-SVG icon set for the ADMIN COCKPIT (Phase 5 item 8) — replaces the
 * functional emoji (🔒 ✅ ⚠️ ↗ ⬆ ✕ ▸ ▾) in Fleet / AdminDashboard / ClientsManager /
 * BuildStatus with crisp, on-token glyphs. Same inline-SVG pattern as BuildStatus's
 * Spinner; strokes use `currentColor`, so an icon inherits its container's text colour
 * (ink / ink-2 / a semantic tone / paper on a solid pill). Decorative wizard/brand/media
 * emoji are deliberately left alone (scope trim iii).
 */
export type IconName =
  | 'lock'
  | 'check'
  | 'warning'
  | 'external'
  | 'arrow-up'
  | 'close'
  | 'chevron-right'
  | 'chevron-down';

const PATHS: Record<IconName, ReactNode> = {
  lock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </>
  ),
  check: <path d="M5 13l4 4L19 7" />,
  warning: (
    <>
      <path d="M12 3.5l8.5 15H3.5z" />
      <path d="M12 10v4" />
      <path d="M12 17h.01" />
    </>
  ),
  external: (
    <>
      <path d="M7 17L17 7" />
      <path d="M8 7h9v9" />
    </>
  ),
  'arrow-up': (
    <>
      <path d="M12 19V5" />
      <path d="M6 11l6-6 6 6" />
    </>
  ),
  close: (
    <>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </>
  ),
  'chevron-right': <path d="M9 6l6 6-6 6" />,
  'chevron-down': <path d="M6 9l6 6 6-6" />,
};

export function Icon({
  name,
  className = 'inline-block h-3.5 w-3.5 align-[-0.15em]',
  title,
}: {
  name: IconName;
  className?: string;
  /** When set, the icon is announced to assistive tech; otherwise it's decorative. */
  title?: string;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
    >
      {title && <title>{title}</title>}
      {PATHS[name]}
    </svg>
  );
}
